import { ref, shallowRef, onUnmounted } from 'vue'

/**
 * The key is an instance setting, not a build-time constant.
 *
 * A Vite variable is inlined at build time, so every operator portal would need
 * its own frontend build to have its own key. Reading it from the shared
 * Inertia props instead means one set of built assets serves every instance.
 *
 * VITE_GOOGLE_MAPS_API_KEY still works as a fallback for one release so
 * existing deployments do not go dark on upgrade.
 */
const DEPRECATED_BUILD_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

let warnedAboutBuildKey = false

// Set from app.js on boot and on every Inertia navigation. Module-level rather
// than usePage() because loadGoogleMapsScript() is called from plain async
// functions, not only from component setup.
let sharedKey = ''

export function setGoogleMapsKey(key) {
    sharedKey = key || ''
}

export function googleMapsApiKey() {
    if (sharedKey) return sharedKey

    if (DEPRECATED_BUILD_KEY && !warnedAboutBuildKey) {
        warnedAboutBuildKey = true
        console.warn(
            'Google Maps is using the deprecated VITE_GOOGLE_MAPS_API_KEY build variable. ' +
            'Set the key under Admin → Instance Settings → Integrations instead; the build ' +
            'variable will be removed in a future release.',
        )
    }

    return DEPRECATED_BUILD_KEY
}

/**
 * What the operator is shown when Maps cannot run.
 */
export const MAPS_UNAVAILABLE = 'Maps unavailable or incorrectly configured'

export const MAPS_NOT_CONFIGURED = 'not-configured'
export const MAPS_UNAVAILABLE_KIND = 'unavailable'

/**
 */
const LOAD_TIMEOUT_MS = 10000

function mapsError(kind, message) {
    const error = new Error(message)
    error.kind = kind

    return error
}

// Track global loading state
let isLoading = false
let isLoaded = false
let loadError = null
let loadTimer = null
const loadCallbacks = []

/**
 * Maps that already finished loading, so a failure arriving later still
 * reaches them.
 */
const failureSubscribers = new Set()

function subscribeToMapsFailure(callback) {
    failureSubscribers.add(callback)

    return () => failureSubscribers.delete(callback)
}

/**
 * Latch a failure and tell everyone waiting on it, once.
 */
function failMaps(error) {
    if (loadTimer) {
        clearTimeout(loadTimer)
        loadTimer = null
    }

    isLoading = false
    isLoaded = false
    loadError = error

    loadCallbacks.forEach(cb => cb.reject(error))
    loadCallbacks.length = 0

    failureSubscribers.forEach(callback => callback(error))
}

/**
 * Load Google Maps script dynamically
 */
export function loadGoogleMapsScript() {
    return new Promise((resolve, reject) => {
        // A key Google has already rejected will be rejected again.
        if (loadError) {
            reject(loadError)
            return
        }

        // Already loaded
        if (isLoaded && window.google?.maps) {
            resolve(window.google.maps)
            return
        }

        // Already loading - queue the callback
        if (isLoading) {
            loadCallbacks.push({ resolve, reject })
            return
        }

        // Check if API key is configured
        const apiKey = googleMapsApiKey()

        if (!apiKey) {
            const error = mapsError(
                MAPS_NOT_CONFIGURED,
                'Google Maps API key is not configured. Set it under Admin → Instance Settings → Integrations.',
            )
            loadError = error
            reject(error)
            return
        }

        isLoading = true
        loadCallbacks.push({ resolve, reject })

        // Create callback function name
        const callbackName = `googleMapsCallback_${Date.now()}`

        const settle = (settleError) => {
            if (settleError) {
                delete window[callbackName]
                failMaps(settleError)

                return
            }

            clearTimeout(loadTimer)
            loadTimer = null
            isLoaded = true
            isLoading = false
            delete window[callbackName]

            loadCallbacks.forEach(cb => cb.resolve(window.google.maps))
            loadCallbacks.length = 0
        }

        // Define the callback
        window[callbackName] = () => settle(null)

        window.gm_authFailure = () => settle(mapsError(MAPS_UNAVAILABLE_KIND, MAPS_UNAVAILABLE))

        // Nothing above fires if the script loads but never boots the API.
        loadTimer = setTimeout(
            () => settle(mapsError(MAPS_UNAVAILABLE_KIND, MAPS_UNAVAILABLE)),
            LOAD_TIMEOUT_MS,
        )

        // Create and inject the script
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&libraries=places&loading=async`
        script.async = true
        script.defer = true
        script.onerror = () => settle(mapsError(MAPS_UNAVAILABLE_KIND, MAPS_UNAVAILABLE))

        document.head.appendChild(script)
    })
}

/**
 * Composable for Google Maps functionality
 */
export function useGoogleMaps() {
    const mapRef = shallowRef(null)
    const markersRef = shallowRef([])
    const isMapLoaded = ref(false)
    const error = ref(null)
    const errorKind = ref(null)
    const mapsApi = shallowRef(null)

    function recordFailure(err) {
        error.value = err.message
        errorKind.value = err.kind || MAPS_UNAVAILABLE_KIND

        isMapLoaded.value = false
    }

    const unsubscribe = subscribeToMapsFailure(recordFailure)

    /**
     * Initialize the map
     * @param {HTMLElement} container - The DOM element to render the map in
     * @param {Object} options - Map options
     */
    async function initMap(container, options = {}) {
        try {
            error.value = null
            errorKind.value = null
            const maps = await loadGoogleMapsScript()
            mapsApi.value = maps

            const defaultOptions = {
                center: { lat: -26.2041, lng: 28.0473 },
                zoom: 12,
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true,
            }

            mapRef.value = new maps.Map(container, {
                ...defaultOptions,
                ...options,
            })

            await new Promise((resolve) => {
                maps.event.addListenerOnce(mapRef.value, 'tilesloaded', resolve)
                // Fallback in case tilesloaded never fires
                setTimeout(resolve, 2000)
            })

            if (errorKind.value) {
                return null
            }

            isMapLoaded.value = true
            return mapRef.value
        } catch (err) {
            recordFailure(err)
            throw err
        }
    }

    /**
     * Add a marker to the map
     */
    function addMarker(position, options = {}) {
        if (!mapRef.value || !mapsApi.value) {
            console.warn('Map not initialized')
            return null
        }

        const marker = new mapsApi.value.Marker({
            position,
            map: mapRef.value,
            ...options,
        })

        markersRef.value.push(marker)
        return marker
    }

    /**
     * Add multiple markers to the map
     */
    function addMarkers(markers) {
        return markers.map(({ position, ...options }) => addMarker(position, options))
    }

    /**
     * Clear all markers from the map
     */
    function clearMarkers() {
        markersRef.value.forEach(marker => {
            try {
                if (mapsApi.value) {
                    mapsApi.value.event.clearInstanceListeners(marker)
                }
                marker.setMap(null)
            } catch {
                // Marker may already be detached
            }
        })
        markersRef.value = []
    }

    /**
     * Fit the map bounds to show all markers
     */
    function fitBoundsToMarkers() {
        if (!mapRef.value || !mapsApi.value || markersRef.value.length === 0) return

        const bounds = new mapsApi.value.LatLngBounds()
        markersRef.value.forEach(marker => {
            bounds.extend(marker.getPosition())
        })
        mapRef.value.fitBounds(bounds)

        // Prevent too much zoom on single marker
        if (markersRef.value.length === 1) {
            mapRef.value.setZoom(15)
        }
    }

    /**
     * Set up a click handler on the map for coordinate picking
     */
    function onMapClick(callback) {
        if (!mapRef.value || !mapsApi.value) return

        mapsApi.value.event.addListener(mapRef.value, 'click', (event) => {
            callback({
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            })
        })
    }

    /**
     * Create an info window
     */
    function createInfoWindow(content) {
        if (!mapsApi.value) return null
        return new mapsApi.value.InfoWindow({ content })
    }

    /**
     * Pan to a specific location
     */
    function panTo(position) {
        if (!mapRef.value) return
        mapRef.value.panTo(position)
    }

    /**
     * Set the zoom level
     */
    function setZoom(zoom) {
        if (!mapRef.value) return
        mapRef.value.setZoom(zoom)
    }

    /**
     * Destroy the map instance and reset all state.
     * Call this when the map container is about to be removed from the DOM
     * (e.g., modal close, tab switch) so the composable can reinitialize
     * cleanly next time.
     */
    function destroyMap() {
        try {
            if (mapRef.value && mapsApi.value) {
                mapsApi.value.event.clearInstanceListeners(mapRef.value)
            }
            clearMarkers()
        } catch {
            // Ignore cleanup errors if DOM is already torn down
        }
        mapRef.value = null
        isMapLoaded.value = false
        error.value = null
        errorKind.value = null
    }

    // Cleanup on unmount
    onUnmounted(() => {
        unsubscribe()
        destroyMap()
        mapsApi.value = null
    })

    return {
        mapRef,
        markersRef,
        isMapLoaded,
        error,
        errorKind,
        mapsApi,
        initMap,
        addMarker,
        addMarkers,
        clearMarkers,
        fitBoundsToMarkers,
        onMapClick,
        createInfoWindow,
        panTo,
        setZoom,
        destroyMap,
    }
}

/**
 * Check if the Google Maps API key is configured
 */
export function isGoogleMapsConfigured() {
    return !!googleMapsApiKey()
}
